import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { APP_ID, type User } from '@funderpro/shared';
import { getToken, getUser, setAuth as saveAuth, API_BASE } from '../../lib/auth';
import { TfCard, TfStatusDot } from '../../components/TfPrimitives';

export function KYCPage() {
  const [user, setUser] = useState<User | null>(getUser());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function startKyc() {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const t = getToken();
      const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/kyc/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to start KYC');
      if (data.inquiry_id) {
        // Production: load Persona widget and submit
        setInfo(`KYC inquiry created (id ${data.inquiry_id}). In production, the Persona widget would open here. Status: ${data.status}`);
      } else if (data.dev_approve) {
        // Dev mode: server auto-approved
        await refreshUser();
        setInfo('KYC verified (dev mode auto-approval).');
      } else {
        setInfo('KYC flow started.');
      }
    } catch (e: any) {
      setErr(e?.message || 'KYC start failed');
    } finally {
      setBusy(false);
    }
  }

  async function refreshUser() {
    const t = getToken();
    const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
    const u = await res.json();
    setUser(u);
    // Re-save with updated kyc_status
    const existing = getUser();
    if (existing) saveAuth({ access_token: t!, refresh_token: '', token_type: 'Bearer', expires_in: 0, user: u });
  }

  useEffect(() => { refreshUser(); }, []);

  return (
    <div className="space-y-4 max-w-2xl p-4" data-testid="kyc-page">
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Verify your identity</h1>
      <p className="px-1 text-[15px] text-tf-text-3">We use Persona to verify your identity. This is required before you can deposit or trade.</p>

      <TfCard className="p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-tf-blue-tint text-tf-blue text-[24px]">🪪</div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Status</div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-[20px] font-bold capitalize text-tf-text-1">{user?.kyc_status || 'none'}</span>
              {user?.kyc_status === 'verified' && <TfStatusDot variant="success" />}
              {user?.kyc_status === 'pending' && <TfStatusDot variant="warning" pulsing />}
              {(!user?.kyc_status || user?.kyc_status === 'none') && <TfStatusDot variant="info" />}
            </div>
          </div>
        </div>

        {err && (
          <div className="mt-4 rounded-[12px] border border-tf-loss/30 bg-tf-red-tint px-3 py-2.5 text-[13px] text-tf-loss" data-testid="kyc-error">
            {err}
          </div>
        )}
        {info && (
          <div className="mt-4 rounded-[12px] border border-tf-blue/30 bg-tf-blue-tint px-3 py-2.5 text-[13px] text-tf-text-1">
            {info}
          </div>
        )}

        {user?.kyc_status === 'verified' ? (
          <div className="mt-4 rounded-[12px] border border-tf-profit/30 bg-tf-green-tint px-3 py-2.5 text-[13px] text-tf-profit flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Your identity is verified. You can deposit and trade.
          </div>
        ) : (
          <button
            onClick={startKyc}
            disabled={busy}
            data-testid="kyc-start"
            className="mt-4 flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white transition active:scale-[0.98] disabled:opacity-40"
          >
            {busy ? 'Starting…' : user?.kyc_status === 'pending' ? 'Resume verification' : 'Start verification'}
          </button>
        )}

        <p className="mt-3 text-[11px] text-tf-text-3">
          Powered by Persona. Your data is encrypted in transit and at rest. Read our <Link to="/privacy-notice" className="text-tf-blue">privacy notice</Link>.
        </p>
      </TfCard>
    </div>
  );
}
